import React, { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import ProductsDropdown from "./ProductsDropdown";
import SolutionsDropdown from "./SolutionsDropdown";
import PlatformsDropdown from "./PlatformsDropdown";
import CompanyDropdown from "./CompanyDropdown";
import { menuItems } from "./menuConfig";

const Header = () => {
  const location = useLocation();
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [showSolutionDropdown, setShowSolutionDropdown] = useState(false);
  const [showPlatformsDropdown, setShowPlatformsDropdown] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const tooltipTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const headerRef = useRef(null);
  const platformsRef = useRef(null);
  const companyRef = useRef(null);
  const signInTooltipRef = useRef(null);
  const signInRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [platformLeftOffset, setPlatformLeftOffset] = useState(0);
  const [companyLeftOffset, setCompanyLeftOffset] = useState(0);

  const closeAllDropdowns = () => {
    setShowProductsDropdown(false);
    setShowSolutionDropdown(false);
    setShowPlatformsDropdown(false);
    setShowCompanyDropdown(false);
    setShowTooltip(false);
  };

  const handleMouseEnter = (label) => {
    setShowTooltip(false);
    setShowProductsDropdown(label === "Products");
    setShowSolutionDropdown(label === "Solutions");
    setShowPlatformsDropdown(label === "Platforms");
    setShowCompanyDropdown(label === "Company");

    if (label === "Platforms" && platformsRef.current && headerRef.current) {
      const rect = platformsRef.current.getBoundingClientRect();
      const headerRect = headerRef.current.getBoundingClientRect();
      setPlatformLeftOffset(rect.left - headerRect.left);
    }

    if (label === "Company" && companyRef.current && headerRef.current) {
      const rect = companyRef.current.getBoundingClientRect();
      const headerRect = headerRef.current.getBoundingClientRect();
      setCompanyLeftOffset(rect.left - headerRect.left);
    }
  };

  const handleMouseLeave = (e) => {
    setTimeout(() => {
      if (
        !headerRef.current?.contains(document.activeElement) &&
        !dropdownRef.current?.contains(document.activeElement) &&
        !signInTooltipRef.current?.contains(document.activeElement)
      ) {
        closeAllDropdowns();
      }
    }, 100);
  };

  const handleMobileDropdownToggle = (label) => {
    // Toggle the specific dropdown instead of just opening it
    if (label === "Products") {
      setShowProductsDropdown(!showProductsDropdown);
      setShowSolutionDropdown(false);
      setShowPlatformsDropdown(false);
      setShowCompanyDropdown(false);
    } else if (label === "Solutions") {
      setShowSolutionDropdown(!showSolutionDropdown);
      setShowProductsDropdown(false);
      setShowPlatformsDropdown(false);
      setShowCompanyDropdown(false);
    } else if (label === "Platforms") {
      setShowPlatformsDropdown(!showPlatformsDropdown);
      setShowProductsDropdown(false);
      setShowSolutionDropdown(false);
      setShowCompanyDropdown(false);
    } else if (label === "Company") {
      setShowCompanyDropdown(!showCompanyDropdown);
      setShowProductsDropdown(false);
      setShowSolutionDropdown(false);
      setShowPlatformsDropdown(false);
    }
    setShowTooltip(false);
  };

  const isProductRoute = location.pathname.startsWith("/product/");
  const isSolutionRoute = location.pathname.startsWith("/solution/");
  const isPlatformRoute = location.pathname.startsWith("/platform/");
  const isCompanyRoute = location.pathname === "/company" || location.pathname.startsWith("/company/");
  const isCustomerRoute = location.pathname.startsWith("/customers");

  const getResponsivePadding = () => {
    if (windowWidth < 480) return 16;
    if (windowWidth < 768) return 20;
    if (windowWidth < 1024) return 30;
    if (windowWidth < 1200) return 40;
    return 60;
  };

  const paddingInline = getResponsivePadding();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
 // Close mobile menu when resizing to desktop
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
        closeAllDropdowns();
      }
      if (companyRef.current && headerRef.current) {
        const rect = companyRef.current.getBoundingClientRect();
        const headerRect = headerRef.current.getBoundingClientRect();
        setCompanyLeftOffset(rect.left - headerRect.left);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    closeAllDropdowns();
  };

  const isMobile = windowWidth < 1024;
  const isTablet = windowWidth >= 1024 && windowWidth <= 1200;

  const getHeaderHeight = () => {
    if (windowWidth < 480) return "82px";
    if (windowWidth < 1024) return "100px";
    return "60px";
  };

  const headerHeight = getHeaderHeight();
  const isHomePage = location.pathname === "/";

  return (
    <div 
      style={{
        position: "fixed",
        top: isHomePage ? "50px" : "0px",
        left: 0,
        width: "100%",
        zIndex: 1000,
        display: "flex",
        justifyContent: "center",
        paddingInline: `${paddingInline}px`,
      }}
      onMouseLeave={handleMouseLeave}
    >
      <header
        ref={headerRef}
        style={{
          backgroundColor: "#FFFFFF",
          width: "100%",
          maxWidth: "1400px",
          height: headerHeight,
          borderRadius: "8px 8px",
          padding: isMobile ? (windowWidth < 480 ? "6px 10px" : "8px 12px") : "12px 14px",
          boxShadow: "0px 4px 4px 0px #00000040",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        <a
          href="/"
          style={{
            display: "inline-flex",
            alignItems: "center",
            textDecoration: "none",
            color: "#000",
            fontWeight: "bold",
            fontSize: windowWidth < 480 ? "16px" : isMobile ? "38px" : "22px",
            flexShrink: 0,
            paddingLeft:isTablet ?"10px":"10px",
          }}
        >
          <img
            src="../img/RavityLogo.png"
            alt="Ravity Logo"
            style={{
              width: windowWidth < 480 ? "20px" : isMobile ? "43px" : "30px",
              height: windowWidth < 480 ? "18px" : isMobile ? "45px" : "28px",
              marginRight: windowWidth < 480 ? "6px" : isMobile ? "18px" : "10px"
            }}
          />
          <span style={{ position: "relative", top: "1px" }}>Ravity</span>
        </a>

        {/* Desktop Navigation */}
        {!isMobile && (
          <ul
            style={{
              display: "flex",
              listStyle: "none",
              gap: isTablet ? "30px" : "60px",
              margin: 0,
              padding: 0,
              flex: 1,
              justifyContent: "center",
            }}
          >
            {menuItems.map((item, index) => {
              const isActive =
                (item.label === "Products" && isProductRoute) ||
                (item.label === "Solutions" && isSolutionRoute) ||
                (item.label === "Platforms" && isPlatformRoute) ||
                (item.label === "Company" && isCompanyRoute) ||
                (item.label === "Customers" && isCustomerRoute);

              const isHoverOnly = item.label === "Platforms" || item.label === "Company";

              const color = isActive ? "#F00D69" : hoveredIndex === index ? "#5D5D5D" : "#000";

              return (
                <li
                  key={index}
                  onMouseEnter={() => {
                    handleMouseEnter(item.label);
                    setHoveredIndex(index);
                  }}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    fontSize: isTablet ? "18px" : "20px",
                    fontWeight: 500
                  }}
                  ref={
                    item.label === "Platforms"
                      ? platformsRef
                      : item.label === "Company"
                        ? companyRef
                        : null
                  }
                >
                  {isHoverOnly ? (
                    <span style={{ color, cursor: "default" }}>{item.label}</span>
                  ) : (
                    <a
                      href={item.href}
                      style={{
                        textDecoration: "none",
                        color,
                        transition: "color 0.2s ease",
                        cursor: "pointer",
                      }}
                    >
                      {item.label}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {/* Mobile hamburger menu */}
        {isMobile && (
          <div
            style={{
              position: "absolute",
              right: windowWidth < 480 ? "10px" : "15px",
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              gap: windowWidth < 480 ? "10px" : "14px",
              zIndex: 10,
              // width: windowWidth < 480 ? "34px" : "38px",   
              height: windowWidth < 480 ? "44px" : "55px",
            }}
          >
            {/* Sign In (left of hamburger) */}
            <a
              href="https://platform.ravity.io"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#F00D69",
                display: "inline-flex",
                alignItems: "center",
                textDecoration: "none",
                fontSize: windowWidth < 480 ? "24px" : "34px",
                marginRight:windowWidth <480 ? "20px":"",
                fontWeight: 600,
                lineHeight: 1,
              }}
            >
              Sign In
              <img
                src="../img/SignInArrow.png"
                alt="Sign In Arrow"
                style={{
                  width: windowWidth < 480 ? "16px" : "38px",
                  height: windowWidth < 480 ? "16px" : "36px",
                  marginLeft: "6px",
                }}
              />
            </a>

            {/* Hamburger */}
            <button
              onClick={toggleMobileMenu}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
              aria-label="Toggle menu"
            >
              <span style={{
                width: windowWidth < 480 ? "18px" : "20px",
                height: "2px",
                backgroundColor: "#000",
                transition: "all 0.3s ease",
                // transform: isMobileMenuOpen ? "rotate(45deg) translate(5px, 5px)" : "none"
              }}></span>
              <span style={{
                width: windowWidth < 480 ? "18px" : "20px",
                height: "2px",
                backgroundColor: "#000",
                transition: "all 0.3s ease",
                // opacity: isMobileMenuOpen ? "0" : "1"
              }}></span>
              <span style={{
                width: windowWidth < 480 ? "18px" : "20px",
                height: "2px",
                backgroundColor: "#000",
                transition: "all 0.3s ease",
              }}></span>
            </button>
          </div>
        )}

        {/* Desktop Sign In and Request Demo */}
        {!isMobile && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: isTablet ? "16px" : "24px",
            flexShrink: 0
          }}>
            <div
              ref={signInRef}
              onMouseEnter={() => {
                clearTimeout(tooltipTimeoutRef.current);
                setShowTooltip(true);
                setShowProductsDropdown(false);
                setShowSolutionDropdown(false);
                setShowPlatformsDropdown(false);
                setShowCompanyDropdown(false);
              }}
              onMouseLeave={() => {
                tooltipTimeoutRef.current = setTimeout(() => {
                  setShowTooltip(false);
                }, 200);
              }}
            >
              <a
                href="https://platform.ravity.io"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#F00D69",
                  display: "inline-flex",
                  alignItems: "center",
                  textDecoration: "none",
                  fontSize: "18px",
                }}
              >
                Sign In
                <img
                  src="../img/SignInArrow.png"
                  alt="Sign In Arrow"
                  style={{
                    width: "21px",
                    height: "21px",
                    marginLeft: "8px"
                  }}
                />
              </a>
            </div>

            <Link
              to="/request-demo"
              style={{
                backgroundColor: "#0A0A14",
                color: "#FFFFFF",
                padding: isTablet ? "10px 16px" : "12px 20px",
                borderRadius: "4px",
                fontSize: "16px",
                fontWeight: 500,
                textDecoration: "none",
                height: isTablet ? "38px" : "44px",
                display: "inline-flex",
                alignItems: "center",
                whiteSpace: "nowrap",
              }}
            >
              Request Demo
            </Link>
          </div>
        )}
      </header>

      {/* Backdrop: tap anywhere outside the panel to close the whole menu */}
      {isMobile && isMobileMenuOpen && (
        <div
          onPointerDown={() => {
            // close EVERYTHING
            setShowProductsDropdown(false);
            setShowSolutionDropdown(false);
            setShowPlatformsDropdown(false);
            setShowCompanyDropdown(false);
            setShowTooltip(false);
            setIsMobileMenuOpen(false);
          }}
          style={{
            position: "fixed",
            inset: 0,          
            background: "transparent",
            zIndex: 1001,     
          }}
          aria-hidden="true"
        />
      )}
      {/* Mobile Menu */}
      {isMobile && isMobileMenuOpen && (
        <div
          style={{
            position: "absolute",
            top: headerHeight,
            left: `${paddingInline}px`,
            right: `${paddingInline}px`,
            backgroundColor: "#fff",
            borderRadius: "0 0 8px 8px",
            boxShadow: "0px 4px 4px 0px #00000040",
            zIndex: 1002,
            padding: windowWidth < 480 ? "12px" : "16px",
            maxHeight: "calc(100vh - 80px)",
            overflowY: "auto",
          }}
        >
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {menuItems.map((item, index) => {
              const isActive =
                (item.label === "Products" && isProductRoute) ||
                (item.label === "Solutions" && isSolutionRoute) ||
                (item.label === "Platforms" && isPlatformRoute) ||
                (item.label === "Company" && isCompanyRoute) ||
                (item.label === "Customers" && isCustomerRoute);

              const hasDropdown = ["Products", "Solutions", "Platforms", "Company"].includes(item.label);
              const isHoverOnly = item.label === "Platforms" || item.label === "Company";

              const isDropdownOpen =
                (item.label === "Products" && showProductsDropdown) ||
                (item.label === "Solutions" && showSolutionDropdown) ||
                (item.label === "Platforms" && showPlatformsDropdown) ||
                (item.label === "Company" && showCompanyDropdown);

              return (
                <li
                  key={index}
                  style={{padding:index === 0
                        ? "0"
                        : windowWidth < 480
                          ? "10px 0"
                          : "12px 0",
                    borderBottom:
                      index === 0
                        ? "none"
                        : index < menuItems.length - 1
                          ? "1px solid #eee"
                          : "none",
                  }}
                >
                  {hasDropdown ? (
                    <div>
                      <button
                        onClick={() => handleMobileDropdownToggle(item.label)}
                        style={{
                          background: "none",
                          border: "none",
                          color: isActive ? "#F00D69" : "#000",
                          fontSize: windowWidth < 480 ? "16px" : "35px",
                          fontWeight: 500,
                          cursor: "pointer",
                          padding: windowWidth < 480 ? "0px":"15px 0 15px 0",
                          textAlign: "left",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>{item.label}</span>
                        <span style={{
                          transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                          fontSize: "16px",
                        }}>
                          ▼
                        </span>
                      </button>

                      {/* Mobile Dropdown Content */}
                      {isDropdownOpen && (
                        <div style={{
                          marginTop: "8px",
                          marginLeft: "16px",
                          padding: "12px",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "8px",
                          border: "1px solid #e9ecef",
                        }}>
                          {item.label === "Products" && <ProductsDropdown />}
                          {item.label === "Solutions" && <SolutionsDropdown />}
                          {item.label === "Platforms" && <PlatformsDropdown />}
                          {item.label === "Company" && <CompanyDropdown />}
                        </div>
                      )}
                    </div>
                  ) : (
                    <a
                      href={item.href}
                      style={{
                        textDecoration: "none",
                        color: isActive ? "#F00D69" : "#000",
                        fontSize: windowWidth < 480 ? "16px" : "35px",
                        padding: windowWidth < 480 ? "0px":"15px 0 15px 0",
                        fontWeight: 500,
                        display: "block",
                      }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>

          <div
            style={{
              marginTop: windowWidth < 480 ? "12px" : "16px",
              paddingTop: windowWidth < 480 ? "12px" : "16px",
              borderTop: "1px solid #eee",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <Link
              to="/request-demo"
              style={{
                backgroundColor: "#0A0A14",
                color: "#FFFFFF",
                padding: windowWidth < 480 ? "10px 16px" : "12px 20px",
                borderRadius: "4px",
                fontSize: windowWidth < 480 ? "16px" : "32px",
                fontWeight: 500,
                textDecoration: "none",
                textAlign: "center",
                display: "block",
              }}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Request Demo
            </Link>
          </div>
        </div>
      )}

      {/* Desktop Tooltips */}
      {!isMobile && showTooltip && (
        <div
          ref={signInTooltipRef}
          onMouseEnter={() => {
            clearTimeout(tooltipTimeoutRef.current);
          }}
          onMouseLeave={() => {
            tooltipTimeoutRef.current = setTimeout(() => {
              setShowTooltip(false);
            }, 200);
          }}
          style={{
            position: "absolute",
            top: headerHeight,
            left: `${companyLeftOffset}px`,
            width: `calc(100% - ${companyLeftOffset + paddingInline}px)`,
            backgroundColor: "#ffffff",
            color: "#777777",
            lineHeight: "100%",
            letterSpacing: "0%",
            borderRadius: "4px",
            padding: "16px 24px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            fontSize: "16px",
            zIndex: 9999,
            fontWeight: "500",
            maxWidth: "1400px",
          }}
        >
          To access our platform, please submit the{" "}
          <span style={{ position: "relative", display: "inline-block", marginRight: "4px" }}>
            <Link
              to="/request-demo"
              style={{
                color: "#1767F4",
                fontWeight: 500,
                textDecoration: "none",
                position: "relative",
                display: "inline-block",
              }}
            >
              Request Access
              <img
                src="/img/borderLine2.png"
                alt="underline"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 15,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  pointerEvents: "none",
                }}
              />
            </Link>
          </span>
          form.
        </div>
      )}
      {/* Desktop-only Dropdowns */}
      {!isMobile && showProductsDropdown && (
        <div ref={dropdownRef} style={dropdownStyle(paddingInline, headerHeight, windowWidth)}>
          <ProductsDropdown />
        </div>
      )}
      {!isMobile && showSolutionDropdown && (
        <div ref={dropdownRef} style={dropdownStyle(paddingInline, headerHeight, windowWidth)}>
          <SolutionsDropdown />
        </div>
      )}
      {!isMobile && showPlatformsDropdown && (
        <div ref={dropdownRef} style={dropdownStyle(platformLeftOffset, headerHeight, windowWidth)}>
          <PlatformsDropdown />
        </div>
      )}
      {!isMobile && showCompanyDropdown && (
        <div ref={dropdownRef} style={dropdownStyle(companyLeftOffset, headerHeight, windowWidth)}>
          <CompanyDropdown />
        </div>
      )}
    </div>
  );
};

const dropdownStyle = (left, headerHeight, windowWidth) => {
  const rightOffset = windowWidth < 480 ? 16 : windowWidth < 768 ? 20 : windowWidth < 1024 ? 30 : windowWidth < 1200 ? 40 : 60;

  return {
    position: 'absolute',
    top: "60px",
    left: `${left}px`,
    right: `${rightOffset}px`,
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0px 4px 4px 0px rgba(249, 245, 245, 0.25)",
    zIndex: 1001,
    maxHeight: "calc(100vh - 120px)",
    overflowY: "auto",
  };
};

export default Header;